import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Alert,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputBase,
    MenuItem,
    Pagination,
    Select,
    TextField,
    Tooltip,
    Toolbar,
    Typography,
    useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EastIcon from '@mui/icons-material/East';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NotesIcon from '@mui/icons-material/Notes';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import UserApi from '../../../../rest-api/user-api';
import CTAUpgradeDialog from '../../../UIControls/CTAUpgradeDialog';
import BlockingSpinner from '../../../UIControls/BlockingSpinner';
import { useUnsavedChanges } from '../../UnsavedChangesContext';
import { useSnackbar } from '../../SnackbarProvider';
import { getCTAUpgradeImageUrl } from '../../../../config';
import { isFreeOrStudentPlan } from '../../../../utils/subscription-plan-utils';
import ASSaveDiskIcon from '../../../../assets/icons/saveDisk.svg';
import {
    buildPlatformCategories,
    deepContainsCategory,
    getPlatformCategoryFilterGroups,
    getSelectedPlatformCategories,
} from '../../../../utils/platform-category-options';
import HandoutImageLoader from '../handout-image-loader';

const locale = 'en-UK';
const OPTIONAL_CATEGORIES_BANNER_MS = 11000;
const IMAGE_PICKER_ITEMS_PER_PAGE_OPTIONS = [6, 9, 12];
const DEFAULT_IMAGE_PICKER_ITEMS_PER_PAGE = 6;
const DEFAULT_TITLE_COLOR = '#A1A1B3';
const EDITED_TITLE_COLOR = '#000453';

const pageTitleSx = {
    color: '#000453',
    fontSize: { xs: '24px', md: '40px' },
    lineHeight: { xs: '24px', md: '40px' },
    fontWeight: 600,
    letterSpacing: 0,
};

const pillButtonSx = {
    height: '50px',
    padding: '15px 20px',
    borderRadius: '200px',
    backgroundColor: '#FF669B',
    color: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '18px',
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 'auto',
    boxShadow: '0px 0px 10px rgba(35, 7, 88, 0.1)',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', color: '#FFFFFF', opacity: 0.65 },
};

const activePillButtonSx = {
    ...pillButtonSx,
    backgroundColor: '#000453',
    '&:hover': { backgroundColor: '#000453' },
    '&.Mui-disabled': { backgroundColor: '#000453', color: '#FFFFFF', opacity: 0.8 },
};

const mobilePillButtonSx = {
    height: 47,
    borderRadius: '188px',
    backgroundColor: '#FF669B',
    color: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    px: { xs: '10px', sm: '14px' },
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: { xs: '13px', sm: '14px' },
    lineHeight: { xs: '13px', sm: '14px' },
    letterSpacing: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 0,
    boxShadow: '0px 0px 9.4px rgba(35, 7, 88, 0.1)',
    '&:hover': { backgroundColor: '#e65588' },
    '&.Mui-disabled': { backgroundColor: '#FF669B', color: '#FFFFFF', opacity: 0.65 },
};

const mobileActivePillButtonSx = {
    ...mobilePillButtonSx,
    backgroundColor: '#000453',
    '&:hover': { backgroundColor: '#000453' },
    '&.Mui-disabled': { backgroundColor: '#000453', color: '#FFFFFF', opacity: 0.8 },
};

const secondaryArrowButtonSx = {
    display: 'inline-flex',
    height: '50px',
    minHeight: '50px',
    pl: '20px',
    pr: '8px',
    py: 0,
    gap: '15.806px',
    borderRadius: '224px',
    background: '#FF669B',
    boxSizing: 'border-box',
    textTransform: 'none',
    fontWeight: 500,
    fontFamily: 'Inter, sans-serif',
    fontSize: { xs: '14px', md: '18px' },
    lineHeight: { xs: '14px', md: '18px' },
    letterSpacing: 0,
    color: '#FFF',
    boxShadow: 'none',
    '&:hover': { background: '#FF85B0' },
    '& .MuiButton-endIcon': {
        marginLeft: 0,
        marginRight: 0,
        width: '34px',
        height: '34px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
};

const imageActionButtonSx = {
    minHeight: '34px',
    px: '12px',
    borderRadius: '200px',
    backgroundColor: '#FFFFFF',
    color: '#616063',
    border: '1px solid #ECECFA',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: '13px',
    boxShadow: '0px 0px 10px rgba(35, 7, 88, 0.1)',
    '&:hover': {
        backgroundColor: '#FFF0F6',
        borderColor: '#FF669B',
        color: '#616063',
    },
};

const addSectionButtonSx = {
    height: '38px',
    px: '18px',
    borderRadius: '200px',
    borderColor: '#FF669B',
    color: '#FF669B',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '14px',
    '&:hover': {
        borderColor: '#FF669B',
        backgroundColor: '#FFF0F6',
    },
};

const arrowIconCircleSx = {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
};

const statusBadgeSx = {
    width: '115px',
    height: '50px',
    backgroundColor: '#CCCCCC',
    borderRadius: '25px',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
};

const createParagraphBlock = (value = '') => ({
    type: 'paragraph',
    value,
});

const createListBlock = (values = ['']) => ({
    type: 'list',
    values,
});

const getDefaultSectionTitle = (index) => (index === 0 ? 'Introduction' : 'New section');

const createSection = (title = 'Introduction', libraryImage = null, isDefaultTitle = true) => ({
    title,
    isDefaultTitle,
    libraryImage,
    blocks: [createParagraphBlock()],
});

const normalizeSectionName = (title, index) => {
    const normalized = (title || `section-${index + 1}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    return normalized || `section-${index + 1}`;
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const getInitialSelections = () =>
    getPlatformCategoryFilterGroups().reduce((accumulator, group) => {
        accumulator[group.key] = [];
        return accumulator;
    }, {});

const getPlainTextFromSections = (sections) =>
    sections
        .flatMap((section) =>
            section.blocks.flatMap((block) => {
                if (block.type === 'paragraph') return block.value || '';
                if (block.type === 'list') return block.values || [];
                return '';
            }),
        )
        .map((value) => value.trim())
        .filter(Boolean)
        .join(' ');

const getKeywordsFromCategories = (categories) =>
    JSON.stringify(categories || {})
        .replace(/[{}[\]",:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const normalizeSearchValue = (value) => {
    if (Array.isArray(value)) return value.map(normalizeSearchValue).join(' ');
    if (value && typeof value === 'object') return Object.values(value).map(normalizeSearchValue).join(' ');
    return (value || '')
        .toString()
        .replace(/&amp;/g, '&')
        .replace(/&nbsp;/g, ' ')
        .toLowerCase();
};

const imageMatchesSearchText = (image, searchText) => {
    const normalizedSearchText = searchText.trim().toLowerCase();
    if (!normalizedSearchText) return true;

    const haystack = [
        image.title,
        image.altText,
        image.imageKey,
        image.sourceContentName,
        image.sourceContentId,
        image.categories,
        image.extractData,
    ]
        .map(normalizeSearchValue)
        .join(' ');

    return haystack.includes(normalizedSearchText);
};

const imageMatchesCategoryFilters = (image, categoryFilters, categoryGroups) => {
    if (!categoryFilters.length) return true;

    return categoryGroups.every((group) => {
        const selectedGroupFilters = group.options.filter((category) => categoryFilters.includes(category));
        if (!selectedGroupFilters.length) return true;

        return selectedGroupFilters.some((category) => deepContainsCategory(image.categories, category));
    });
};

const buildContentData = (title, sections) => ({
    [locale]: sections.map((section, sectionIndex) => {
        const blocks = [];

        if (sectionIndex === 0) {
            blocks.push({
                type: 'documentTitle',
                value: title.trim(),
            });

            if (section.libraryImage?.imageKey) {
                blocks.push({
                    type: 'illustration',
                    src: section.libraryImage.imageKey,
                    alt: section.libraryImage.altText || section.libraryImage.title || title.trim(),
                });
            }
        }

        if (section.title?.trim() && !section.isDefaultTitle) {
            blocks.push({
                type: 'sectionTitle',
                value: section.title.trim(),
            });
        }

        if (sectionIndex !== 0 && section.libraryImage?.imageKey) {
            blocks.push({
                type: 'illustration',
                src: section.libraryImage.imageKey,
                alt: section.libraryImage.altText || section.libraryImage.title || title.trim(),
            });
        }

        section.blocks.forEach((block) => {
            if (block.type === 'paragraph' && block.value.trim()) {
                blocks.push(createParagraphBlock(block.value.trim()));
            }
            if (block.type === 'list') {
                const values = block.values.map((value) => value.trim()).filter(Boolean);
                if (values.length > 0) {
                    blocks.push(createListBlock(values));
                }
            }
        });

        return {
            sectionName: normalizeSectionName(section.isDefaultTitle ? '' : section.title, sectionIndex),
            blocks,
        };
    }),
});

const parseRecordSections = (contentData) => {
    const localeData = contentData?.[locale];
    if (!Array.isArray(localeData) || localeData.length === 0) return [createSection()];

    return localeData.map((section, index) => {
        const blocks = section.blocks || [];
        const sectionTitle = blocks.find((block) => block.type === 'sectionTitle')?.value || '';
        const illustrationBlock = blocks.find((block) => block.type === 'illustration');
        const editableBlocks = blocks
            .filter((block) => block.type === 'paragraph' || block.type === 'list')
            .map((block) => {
                if (block.type === 'list') return createListBlock(block.values || ['']);
                return createParagraphBlock(block.value || '');
            });

        return {
            title: sectionTitle || getDefaultSectionTitle(index),
            isDefaultTitle: !sectionTitle,
            libraryImage: illustrationBlock
                ? {
                      imageKey: illustrationBlock.src,
                      title: illustrationBlock.alt || 'Illustration',
                      altText: illustrationBlock.alt || '',
                  }
                : null,
            blocks: editableBlocks.length > 0 ? editableBlocks : [createParagraphBlock()],
        };
    });
};

const getMainLibraryImageFromSections = (sections) => sections?.[0]?.libraryImage || null;
const getFirstLibraryImageFromSections = (sections) =>
    sections?.find((section) => section.libraryImage?.imageKey)?.libraryImage || null;

const getDocumentTitleFromRecord = (record) => {
    const blocks = (record?.contentData?.[locale] || []).flatMap((section) => section.blocks || []);
    return blocks.find((block) => block.type === 'documentTitle')?.value || record?.title || record?.contentName || '';
};

const BuildYourOwnHandoutEditor = observer(() => {
    const { handoutId } = useParams();
    const navigate = useNavigate();
    const { authStore, userDataStore, userCreatedHandoutStore, imageStore, imageLibraryStore } = useContext(StoreContext);
    const { setHasUnsavedChanges, promptBeforeAction, setOnSave } = useUnsavedChanges();
    const showSnackbar = useSnackbar();
    const saveRef = useRef(null);
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const [title, setTitle] = useState('');
    const [sections, setSections] = useState([createSection()]);
    const [categorySelections, setCategorySelections] = useState(getInitialSelections());
    const [contentStatus, setContentStatus] = useState('unpublished');
    const [savedHandoutId, setSavedHandoutId] = useState(handoutId || null);
    const [alphabeticalName, setAlphabeticalName] = useState('');
    const [description, setDescription] = useState('');
    const [keywords, setKeywords] = useState('');
    const [metadataTouched, setMetadataTouched] = useState(false);
    const [imagePickerOpen, setImagePickerOpen] = useState(false);
    const [imagePickerTargetSectionIndex, setImagePickerTargetSectionIndex] = useState(0);
    const [librarySearchText, setLibrarySearchText] = useState('');
    const [libraryCategoryFilters, setLibraryCategoryFilters] = useState([]);
    const [libraryPage, setLibraryPage] = useState(1);
    const [libraryItemsPerPage, setLibraryItemsPerPage] = useState(DEFAULT_IMAGE_PICKER_ITEMS_PER_PAGE);
    const [localLibraryImages, setLocalLibraryImages] = useState([]);
    const [isLibraryFetchLoading, setIsLibraryFetchLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [showOptionalCategoriesBanner, setShowOptionalCategoriesBanner] = useState(true);

    const categoryGroups = useMemo(() => getPlatformCategoryFilterGroups(), []);
    const categories = useMemo(() => buildPlatformCategories(categorySelections), [categorySelections]);
    const mainLibraryImage = useMemo(() => getMainLibraryImageFromSections(sections), [sections]);
    const currentHandoutId = handoutId || savedHandoutId;
    const canPublish = Boolean(currentHandoutId);
    const libraryImages = imageLibraryStore?.isInitialized ? imageLibraryStore.images : localLibraryImages;
    const isLibraryLoading = Boolean(imageLibraryStore?.isLoading || isLibraryFetchLoading);
    const filteredLibraryImages = useMemo(
        () =>
            (libraryImages || []).filter(
                (image) =>
                    imageMatchesSearchText(image, librarySearchText) &&
                    imageMatchesCategoryFilters(image, libraryCategoryFilters, categoryGroups),
            ),
        [categoryGroups, libraryCategoryFilters, libraryImages, librarySearchText],
    );
    const libraryPageCount = Math.max(1, Math.ceil(filteredLibraryImages.length / libraryItemsPerPage));
    const libraryPageStartIndex = (libraryPage - 1) * libraryItemsPerPage;
    const paginatedLibraryImages = useMemo(
        () => filteredLibraryImages.slice(libraryPageStartIndex, libraryPageStartIndex + libraryItemsPerPage),
        [filteredLibraryImages, libraryItemsPerPage, libraryPageStartIndex],
    );
    const previewContentData = useMemo(
        () => buildContentData(title || 'Untitled handout', sections),
        [sections, title],
    );
    const previewHandoutData = useMemo(
        () => ({
            contentId: currentHandoutId || 'build-your-own-preview',
            contentName: title || 'Untitled handout',
            contentStatus,
            categories,
            contentData: previewContentData,
            selectedLibraryImage: mainLibraryImage,
        }),
        [categories, contentStatus, currentHandoutId, mainLibraryImage, previewContentData, title],
    );

    const markUnsaved = () => setHasUnsavedChanges(true);

    const openImagePicker = (sectionIndex) => {
        setImagePickerTargetSectionIndex(sectionIndex);
        setImagePickerOpen(true);
    };

    const removeIllustration = (sectionIndex) => {
        updateSection(sectionIndex, (currentSection) => ({
            ...currentSection,
            libraryImage: null,
        }));
    };

    const toggleLibraryCategoryFilter = (category) => {
        setLibraryCategoryFilters((current) =>
            current.includes(category) ? current.filter((item) => item !== category) : [...current, category],
        );
    };

    useEffect(() => {
        if (handoutId) setSavedHandoutId(handoutId);
    }, [handoutId]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setShowOptionalCategoriesBanner(false);
        }, OPTIONAL_CATEGORIES_BANNER_MS);

        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (metadataTouched) return;
        setAlphabeticalName(title);
        setDescription(getPlainTextFromSections(sections).slice(0, 500));
        setKeywords([title, mainLibraryImage?.title, getKeywordsFromCategories(categories)].filter(Boolean).join(' '));
    }, [categories, mainLibraryImage?.title, metadataTouched, sections, title]);

    useEffect(() => {
        const loadHandout = async () => {
            if (!handoutId) return;
            setIsLoading(true);
            try {
                const cached = await userCreatedHandoutStore?.getUserCreatedHandoutById(handoutId);
                const response = cached ? null : await UserApi.getUserCreatedHandout(authStore.idToken, handoutId);
                const record = cached || response?.userCreatedHandout || response?.handout;
                if (!record) return;

                setTitle(getDocumentTitleFromRecord(record));
                const parsedSections = parseRecordSections(record.contentData);
                setSections(parsedSections);
                setCategorySelections({ ...getInitialSelections(), ...getSelectedPlatformCategories(record.categories) });
                setContentStatus(record.contentStatus === 'published' ? 'published' : 'unpublished');
                setAlphabeticalName(record.alphabeticalName || record.title || record.contentName || '');
                setDescription(record.extractData?.['en-GB']?.description || '');
                setKeywords(record.extractData?.['en-GB']?.keywords || '');
                setMetadataTouched(true);
            } catch (error) {
                console.error('Failed to load user-created handout:', error);
                showSnackbar('Failed to load handout. Please try again.', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        loadHandout();
    }, [authStore.idToken, handoutId, showSnackbar, userCreatedHandoutStore]);

    useEffect(() => {
        if (!imagePickerOpen) return;
        let isMounted = true;

        const fetchImages = async () => {
            setIsLibraryFetchLoading(true);
            try {
                const images = imageLibraryStore
                    ? await imageLibraryStore.fetchImageLibrary()
                    : (await UserApi.getImageLibrary(authStore.idToken))?.images || [];
                if (isMounted) setLocalLibraryImages(images || []);
            } catch (error) {
                console.error('Failed to load image library:', error);
                if (isMounted) {
                    showSnackbar(
                        'Failed to load image library. Please try again or contact support if the problem continues.',
                        'error',
                    );
                }
            } finally {
                if (isMounted) setIsLibraryFetchLoading(false);
            }
        };

        fetchImages();
        return () => {
            isMounted = false;
        };
    }, [authStore.idToken, imageLibraryStore, imagePickerOpen, showSnackbar]);

    useEffect(() => {
        setLibraryPage(1);
    }, [imagePickerOpen, libraryCategoryFilters, libraryItemsPerPage, librarySearchText]);

    useEffect(() => {
        if (libraryPage > libraryPageCount) {
            setLibraryPage(libraryPageCount);
        }
    }, [libraryPage, libraryPageCount]);

    const updateSection = (sectionIndex, updater) => {
        markUnsaved();
        setSections((current) =>
            current.map((section, index) => (index === sectionIndex ? updater(clone(section)) : section)),
        );
    };

    const updateBlock = (sectionIndex, blockIndex, updater) => {
        updateSection(sectionIndex, (section) => {
            section.blocks = section.blocks.map((block, index) => (index === blockIndex ? updater(clone(block)) : block));
            return section;
        });
    };

    const moveSection = (sectionIndex, direction) => {
        markUnsaved();
        setSections((current) => {
            const nextIndex = sectionIndex + direction;
            if (nextIndex < 0 || nextIndex >= current.length) return current;
            const next = [...current];
            [next[sectionIndex], next[nextIndex]] = [next[nextIndex], next[sectionIndex]];
            return next;
        });
    };

    const moveBlock = (sectionIndex, blockIndex, direction) => {
        updateSection(sectionIndex, (section) => {
            const nextIndex = blockIndex + direction;
            if (nextIndex < 0 || nextIndex >= section.blocks.length) return section;
            [section.blocks[blockIndex], section.blocks[nextIndex]] = [
                section.blocks[nextIndex],
                section.blocks[blockIndex],
            ];
            return section;
        });
    };

    const toggleCategoryValue = (groupKey, value) => {
        markUnsaved();
        setCategorySelections((current) => {
            const values = current[groupKey] || [];
            return {
                ...current,
                [groupKey]: values.includes(value) ? values.filter((item) => item !== value) : [...values, value],
            };
        });
    };

    const validate = () => {
        if (!title.trim()) return 'Title is required.';
        if (!getPlainTextFromSections(sections)) return 'At least one paragraph or list item is required.';
        return null;
    };

    const save = async (nextStatus = contentStatus) => {
        const validationError = validate();
        if (validationError) {
            showSnackbar(validationError, 'warning');
            return;
        }

        if (isFreeOrStudentPlan(userDataStore?.subscriptionData?.accountType)) {
            setUpgradeCTAOpen(true);
            return;
        }

        const trimmedTitle = title.trim();
        const contentData = buildContentData(trimmedTitle, sections);
        const selectedImage = getFirstLibraryImageFromSections(sections);
        const resolvedDescription = description.trim() || getPlainTextFromSections(sections).slice(0, 500);
        const payload = {
            ...(currentHandoutId ? { handoutId: currentHandoutId } : {}),
            title: trimmedTitle,
            contentData,
            categories,
            selectedLibraryImage: selectedImage,
            contentStatus: nextStatus,
            contentName: trimmedTitle,
            alphabeticalName: alphabeticalName.trim() || trimmedTitle,
            extractData: {
                'en-GB': {
                    alphabeticalName: alphabeticalName.trim() || trimmedTitle,
                    contentName: trimmedTitle,
                    description: resolvedDescription,
                    keywords: keywords.trim(),
                },
            },
        };

        setIsSaving(true);
        try {
            const result = await UserApi.createOrUpdateUserCreatedHandout(authStore.idToken, payload);
            const savedHandout = result.handout || { ...payload, handoutId: result.handoutId };
            await userCreatedHandoutStore.addOrUpdateUserCreatedHandout(savedHandout);
            setSavedHandoutId(savedHandout.handoutId);
            setContentStatus(savedHandout.contentStatus || nextStatus);
            setHasUnsavedChanges(false);
            showSnackbar(
                nextStatus === 'published' ? 'Handout published successfully!' : 'Handout successfully saved!',
                'success',
            );
            if (!handoutId && savedHandout.handoutId) {
                navigate(`/build-handout/${savedHandout.handoutId}`, { replace: true });
            }
        } catch (error) {
            console.error('Failed to save user-created handout:', error);
            showSnackbar('Failed to save handout. Please try again.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    saveRef.current = save;

    useEffect(() => {
        setOnSave(() => () => saveRef.current?.('unpublished'));
        return () => setOnSave(null);
    }, [setOnSave]);

    const renderBlockEditor = (block, sectionIndex, blockIndex) => (
        <Box
            key={`section-${sectionIndex}-block-${blockIndex}`}
            sx={{
                border: '1px dashed rgba(0, 4, 83, 0.22)',
                borderRadius: '8px',
                p: 1,
                mb: 1,
                '&:hover': { background: 'rgba(0, 4, 83, 0.02)' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, background: '#F2F2F8', p: 0.5, mb: 1 }}>
                <Tooltip title="Edit block">
                    <IconButton size="small" sx={{ color: '#000453' }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete block">
                    <IconButton
                        size="small"
                        onClick={() =>
                            updateSection(sectionIndex, (section) => {
                                section.blocks = section.blocks.filter((_, index) => index !== blockIndex);
                                if (section.blocks.length === 0) section.blocks.push(createParagraphBlock());
                                return section;
                            })
                        }
                        sx={{ color: '#d32f2f' }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Move up">
                    <span>
                        <IconButton
                            size="small"
                            disabled={blockIndex === 0}
                            onClick={() => moveBlock(sectionIndex, blockIndex, -1)}
                        >
                            <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Move down">
                    <span>
                        <IconButton
                            size="small"
                            disabled={blockIndex === sections[sectionIndex].blocks.length - 1}
                            onClick={() => moveBlock(sectionIndex, blockIndex, 1)}
                        >
                            <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {block.type === 'paragraph' ? (
                <TextField
                    value={block.value}
                    onChange={(event) =>
                        updateBlock(sectionIndex, blockIndex, (currentBlock) => ({
                            ...currentBlock,
                            value: event.target.value,
                        }))
                    }
                    multiline
                    minRows={3}
                    fullWidth
                    placeholder="Write a paragraph..."
                />
            ) : (
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {block.values.map((value, itemIndex) => (
                        <Box component="li" key={`list-${itemIndex}`} sx={{ mb: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    value={value}
                                    onChange={(event) =>
                                        updateBlock(sectionIndex, blockIndex, (currentBlock) => {
                                            currentBlock.values[itemIndex] = event.target.value;
                                            return currentBlock;
                                        })
                                    }
                                    fullWidth
                                    size="small"
                                    placeholder="List item..."
                                />
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        updateBlock(sectionIndex, blockIndex, (currentBlock) => {
                                            currentBlock.values = currentBlock.values.filter(
                                                (_, index) => index !== itemIndex,
                                            );
                                            if (currentBlock.values.length === 0) currentBlock.values.push('');
                                            return currentBlock;
                                        })
                                    }
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                    <Button
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={() =>
                            updateBlock(sectionIndex, blockIndex, (currentBlock) => {
                                currentBlock.values.push('');
                                return currentBlock;
                            })
                        }
                    >
                        Add list item
                    </Button>
                </Box>
            )}
        </Box>
    );

    return (
        <Box
            sx={{
                background: '#F0F0FC',
                minHeight: 'calc(100vh - 140px)',
                p: { xs: 2, md: 4 },
                pb: { xs: '112px', md: 4 },
            }}
        >
            <BlockingSpinner open={isLoading || isSaving} />
            <Box sx={{ maxWidth: 1320, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <Box>
                        <Typography sx={pageTitleSx}>Build a handout</Typography>
                        <Button
                            variant="contained"
                            onClick={() => promptBeforeAction(() => navigate('/my-library'))}
                            endIcon={
                                <Box sx={arrowIconCircleSx}>
                                    <EastIcon sx={{ color: '#FF669B', fontSize: '23px' }} />
                                </Box>
                            }
                            sx={{
                                ...secondaryArrowButtonSx,
                                display: { xs: 'inline-flex', md: 'none' },
                                mt: 2,
                            }}
                        >
                            My handouts
                        </Button>
                    </Box>
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={() => promptBeforeAction(() => navigate('/my-library'))}
                            endIcon={
                                <Box sx={arrowIconCircleSx}>
                                    <EastIcon sx={{ color: '#FF669B', fontSize: { xs: '23px', md: '26px' } }} />
                                </Box>
                            }
                            sx={secondaryArrowButtonSx}
                        >
                            My handouts
                        </Button>
                        <Button
                            onClick={() => promptBeforeAction(() => navigate(`/my-handout/${currentHandoutId}`))}
                            disabled={!currentHandoutId}
                            sx={pillButtonSx}
                        >
                            <VisibilityIcon sx={{ fontSize: 22 }} />
                            Preview
                        </Button>
                        <Button
                            onClick={() => save(contentStatus)}
                            disabled={isSaving}
                            sx={isSaving ? activePillButtonSx : pillButtonSx}
                        >
                            <img src={ASSaveDiskIcon} alt="" style={{ width: 20, height: 22 }} />
                            Save handout
                        </Button>
                        <Button
                            onClick={() => save(contentStatus === 'published' ? 'unpublished' : 'published')}
                            disabled={!canPublish || isSaving}
                            sx={isSaving ? activePillButtonSx : pillButtonSx}
                        >
                            {contentStatus === 'published' ? (
                                <VisibilityOffIcon sx={{ fontSize: 22 }} />
                            ) : (
                                <PublishIcon sx={{ fontSize: 22 }} />
                            )}
                            {contentStatus === 'published' ? 'Unpublish' : 'Publish'}
                        </Button>
                    </Box>
                </Box>

                <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                        <Box
                            sx={{
                                background: '#FFFFFF',
                                borderRadius: '30px',
                                p: { xs: 2, md: 3 },
                                border: '1px solid #ECECFA',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', mb: 3 }}>
                                <TextField
                                    value={title}
                                    onChange={(event) => {
                                        markUnsaved();
                                        setTitle(event.target.value);
                                    }}
                                    fullWidth
                                    placeholder={isMobile ? 'Title' : 'Handout title'}
                                    variant="standard"
                                    InputProps={{
                                        disableUnderline: true,
                                        sx: {
                                            fontSize: { xs: '30pt', md: '35pt' },
                                            lineHeight: 1.15,
                                            fontWeight: 700,
                                            '& .MuiInputBase-input': {
                                                color: title.trim() ? EDITED_TITLE_COLOR : DEFAULT_TITLE_COLOR,
                                            },
                                            '& .MuiInputBase-input::placeholder': {
                                                color: DEFAULT_TITLE_COLOR,
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                />
                                <Box sx={statusBadgeSx}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontWeight: 400,
                                            fontSize: { xs: '0.85rem', sm: '1rem' },
                                            color: '#FFFFFF',
                                            textTransform: 'none',
                                        }}
                                    >
                                        {contentStatus === 'published' ? 'Published' : 'Unpublished'}
                                    </Typography>
                                </Box>
                            </Box>

                            {sections.map((section, sectionIndex) => (
                                <Box key={`section-${sectionIndex}`} sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TextField
                                            value={section.title}
                                            onChange={(event) =>
                                                updateSection(sectionIndex, (currentSection) => ({
                                                    ...currentSection,
                                                    title: event.target.value,
                                                    isDefaultTitle: false,
                                                }))
                                            }
                                            onFocus={(event) => {
                                                if (section.isDefaultTitle) event.target.select();
                                            }}
                                            variant="standard"
                                            fullWidth
                                            placeholder="Section title"
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: {
                                                    fontSize: '20pt',
                                                    lineHeight: 1.2,
                                                    fontWeight: 700,
                                                    '& .MuiInputBase-input': {
                                                        color: section.isDefaultTitle
                                                            ? DEFAULT_TITLE_COLOR
                                                            : EDITED_TITLE_COLOR,
                                                    },
                                                    '& .MuiInputBase-input::placeholder': {
                                                        color: DEFAULT_TITLE_COLOR,
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                        />
                                        <Tooltip title="Move section up">
                                            <span>
                                                <IconButton
                                                    disabled={sectionIndex === 0}
                                                    onClick={() => moveSection(sectionIndex, -1)}
                                                >
                                                    <KeyboardArrowUpIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Move section down">
                                            <span>
                                                <IconButton
                                                    disabled={sectionIndex === sections.length - 1}
                                                    onClick={() => moveSection(sectionIndex, 1)}
                                                >
                                                    <KeyboardArrowDownIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Delete section">
                                            <span>
                                                <IconButton
                                                    disabled={sections.length === 1}
                                                    onClick={() => {
                                                        markUnsaved();
                                                        setSections((current) =>
                                                            current.filter((_, index) => index !== sectionIndex),
                                                        );
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>

                                    {section.libraryImage?.imageKey && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                            <Box sx={{ textAlign: 'center', width: '100%', position: 'relative' }}>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        right: 0,
                                                        display: 'flex',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                        justifyContent: 'flex-end',
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        startIcon={<ImageIcon />}
                                                        onClick={() => openImagePicker(sectionIndex)}
                                                        sx={imageActionButtonSx}
                                                    >
                                                        Replace
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => removeIllustration(sectionIndex)}
                                                        sx={imageActionButtonSx}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Box>
                                                <Box sx={{ height: 360, display: 'flex', justifyContent: 'center' }}>
                                                    <HandoutImageLoader
                                                        src={section.libraryImage.imageKey}
                                                        alt={section.libraryImage.altText || section.libraryImage.title}
                                                        imageStore={imageStore}
                                                        handoutData={previewHandoutData}
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '100%',
                                                            objectFit: 'contain',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}

                                    {section.blocks.map((block, blockIndex) =>
                                        renderBlockEditor(block, sectionIndex, blockIndex),
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            startIcon={<NotesIcon />}
                                            onClick={() =>
                                                updateSection(sectionIndex, (currentSection) => {
                                                    currentSection.blocks.push(createParagraphBlock());
                                                    return currentSection;
                                                })
                                            }
                                        >
                                            Add paragraph
                                        </Button>
                                        <Button
                                            startIcon={<FormatListBulletedIcon />}
                                            onClick={() =>
                                                updateSection(sectionIndex, (currentSection) => {
                                                    currentSection.blocks.push(createListBlock());
                                                    return currentSection;
                                                })
                                            }
                                        >
                                            Add bullet list
                                        </Button>
                                        {!section.libraryImage?.imageKey && (
                                            <Button startIcon={<ImageIcon />} onClick={() => openImagePicker(sectionIndex)}>
                                                Add illustration
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            ))}

                            <Button
                                startIcon={<AddIcon />}
                                variant="outlined"
                                sx={addSectionButtonSx}
                                onClick={() => {
                                    markUnsaved();
                                    setSections((current) => [...current, createSection('New section')]);
                                }}
                            >
                                Add section
                            </Button>

                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box sx={{ background: '#FFFFFF', borderRadius: '20px', p: 2.5, border: '1px solid #ECECFA', mb: 2 }}>
                            <Collapse in={showOptionalCategoriesBanner} unmountOnExit>
                                <Alert
                                    severity="info"
                                    sx={{
                                        mb: 2,
                                        color: '#000453',
                                        backgroundColor: '#F0F0FC',
                                        '& .MuiAlert-icon': { color: '#FF669B' },
                                    }}
                                >
                                    Categories are optional.
                                </Alert>
                            </Collapse>
                            <Typography sx={{ color: '#000453', fontSize: 18, fontWeight: 700, mb: 1 }}>
                                Categorise your handout
                            </Typography>
                            <Typography sx={{ color: '#616063', fontSize: 14, lineHeight: '20px', mb: 2 }}>
                                Select one or more categories to help you and your team find this handout when searching.
                            </Typography>
                            {categoryGroups.map((group) => (
                                <Box key={group.key} sx={{ mb: 1.5 }}>
                                    <Typography sx={{ color: '#616063', fontWeight: 700, mb: 0.5 }}>
                                        {group.title}
                                    </Typography>
                                    {group.options.map((option) => (
                                        <FormControlLabel
                                            key={option}
                                            control={
                                                <Checkbox
                                                    checked={(categorySelections[group.key] || []).includes(option)}
                                                    onChange={() => toggleCategoryValue(group.key, option)}
                                                />
                                            }
                                            label={option}
                                            sx={{ display: 'block' }}
                                        />
                                    ))}
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ background: '#FFFFFF', borderRadius: '20px', p: 2.5, border: '1px solid #ECECFA' }}>
                            <Typography sx={{ color: '#000453', fontSize: 18, fontWeight: 700, mb: 1 }}>
                                Add search details
                            </Typography>
                            <TextField
                                label="Alphabetical name"
                                value={alphabeticalName}
                                onChange={(event) => {
                                    markUnsaved();
                                    setMetadataTouched(true);
                                    setAlphabeticalName(event.target.value);
                                }}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Description"
                                value={description}
                                onChange={(event) => {
                                    markUnsaved();
                                    setMetadataTouched(true);
                                    setDescription(event.target.value);
                                }}
                                multiline
                                minRows={3}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Keywords"
                                value={keywords}
                                onChange={(event) => {
                                    markUnsaved();
                                    setMetadataTouched(true);
                                    setKeywords(event.target.value);
                                }}
                                multiline
                                minRows={2}
                                fullWidth
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            <AppBar
                position="fixed"
                color="default"
                sx={{
                    display: { xs: 'block', md: 'none' },
                    top: 'auto',
                    bottom: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    borderTop: '1px solid #E5E5E5',
                    px: '14px',
                    pb: 'env(safe-area-inset-bottom)',
                    '@media print': { display: 'none' },
                }}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '6px',
                        minHeight: '85px',
                        px: 0,
                    }}
                >
                    <Button
                        onClick={() => promptBeforeAction(() => navigate(`/my-handout/${currentHandoutId}`))}
                        disabled={!currentHandoutId}
                        sx={{ ...mobilePillButtonSx, flex: '0.95 1 0' }}
                    >
                        <VisibilityIcon sx={{ fontSize: 17 }} />
                        Preview
                    </Button>
                    <Button
                        onClick={() => save(contentStatus)}
                        disabled={isSaving}
                        sx={{ ...(isSaving ? mobileActivePillButtonSx : mobilePillButtonSx), flex: '1.25 1 0' }}
                    >
                        <img src={ASSaveDiskIcon} alt="" style={{ width: 15, height: 17 }} />
                        Save handout
                    </Button>
                    <Button
                        onClick={() => save(contentStatus === 'published' ? 'unpublished' : 'published')}
                        disabled={!canPublish || isSaving}
                        sx={{ ...(isSaving ? mobileActivePillButtonSx : mobilePillButtonSx), flex: '1.2 1 0' }}
                    >
                        {contentStatus === 'published' ? (
                            <VisibilityOffIcon sx={{ fontSize: 17 }} />
                        ) : (
                            <PublishIcon sx={{ fontSize: 17 }} />
                        )}
                        {contentStatus === 'published' ? 'Unpublish' : 'Publish'}
                    </Button>
                </Toolbar>
            </AppBar>

            <Dialog open={imagePickerOpen} onClose={() => setImagePickerOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Select an illustration
                    <IconButton onClick={() => setImagePickerOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SearchIcon />
                        <InputBase
                            value={librarySearchText}
                            onChange={(event) => setLibrarySearchText(event.target.value)}
                            placeholder="Search image library..."
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        {categoryGroups.map((group) => (
                            <Box key={`image-filter-${group.key}`} sx={{ mb: 1 }}>
                                <Typography sx={{ color: '#616063', fontWeight: 700, fontSize: 13, mb: 0.5 }}>
                                    {group.title}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {group.options.map((option) => (
                                        <Button
                                            key={option}
                                            size="small"
                                            variant={libraryCategoryFilters.includes(option) ? 'contained' : 'outlined'}
                                            onClick={() => toggleLibraryCategoryFilter(option)}
                                            sx={{
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                borderColor: '#FF669B',
                                                color: libraryCategoryFilters.includes(option) ? '#FFFFFF' : '#616063',
                                                backgroundColor: libraryCategoryFilters.includes(option)
                                                    ? '#FF669B'
                                                    : '#FFFFFF',
                                                '&:hover': {
                                                    borderColor: '#FF669B',
                                                    backgroundColor: libraryCategoryFilters.includes(option)
                                                        ? '#e65588'
                                                        : '#FFF0F6',
                                                },
                                            }}
                                        >
                                            {option}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                        {libraryCategoryFilters.length > 0 && (
                            <Button
                                size="small"
                                onClick={() => setLibraryCategoryFilters([])}
                                sx={{ color: '#FF669B', textTransform: 'none', mt: 0.5 }}
                            >
                                Clear category filters
                            </Button>
                        )}
                    </Box>
                    {isLibraryLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                            <CircularProgress sx={{ color: '#FF669B' }} />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                {paginatedLibraryImages.map((image) => {
                                    const imageTitle = image.title || image.altText || 'Untitled image';

                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={image.imageId || image.imageKey}
                                            sx={{ display: 'flex' }}
                                        >
                                            <Box
                                                data-testid={`image-library-card-${image.imageId || image.imageKey}`}
                                                onClick={() => {
                                                    markUnsaved();
                                                    updateSection(imagePickerTargetSectionIndex, (currentSection) => ({
                                                        ...currentSection,
                                                        libraryImage: image,
                                                    }));
                                                    setImagePickerOpen(false);
                                                }}
                                                sx={{
                                                    width: '100%',
                                                    minHeight: 255,
                                                    border: '1px solid #ECECFA',
                                                    borderRadius: '12px',
                                                    p: 1.5,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    '&:hover': { borderColor: '#FF669B' },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        height: 184,
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        mb: 1.5,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <HandoutImageLoader
                                                        src={image.imageKey}
                                                        alt={image.altText || image.title}
                                                        imageStore={imageStore}
                                                        handoutData={{
                                                            contentId: image.imageId || image.imageKey,
                                                            contentData: buildContentData(image.title || 'Image', [
                                                                createSection('', image),
                                                            ]),
                                                        }}
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '100%',
                                                            objectFit: 'contain',
                                                        }}
                                                    />
                                                </Box>
                                                <Tooltip title={imageTitle} arrow>
                                                    <Typography
                                                        sx={{
                                                            color: '#000453',
                                                            fontWeight: 700,
                                                            minHeight: 48,
                                                            lineHeight: '24px',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {imageTitle}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            {filteredLibraryImages.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        mt: 2,
                                    }}
                                >
                                    <Pagination
                                        count={libraryPageCount}
                                        page={libraryPage}
                                        onChange={(event, page) => setLibraryPage(page)}
                                        sx={{
                                            '& .MuiPaginationItem-root.Mui-selected': {
                                                backgroundColor: '#FF669B',
                                                color: '#FFFFFF',
                                            },
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ color: '#616063', fontSize: 13 }}>Items per page</Typography>
                                        <Select
                                            size="small"
                                            value={libraryItemsPerPage}
                                            onChange={(event) => setLibraryItemsPerPage(Number(event.target.value))}
                                        >
                                            {IMAGE_PICKER_ITEMS_PER_PAGE_OPTIONS.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                    {!isLibraryLoading && filteredLibraryImages.length === 0 && (
                        <Typography sx={{ color: '#616063', py: 3, textAlign: 'center' }}>
                            No images available.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock Build Your Own handouts with just one click!"
                message="Saving Build Your Own handouts is beyond your current plan. Upgrade today to save and publish your handouts."
                buttonText="Upgrade plan"
                benefits={[
                    'Save your own handouts',
                    'Publish handouts for search and sharing',
                    'Personalise patient education content',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
                plan="professional"
            />
        </Box>
    );
});

export default BuildYourOwnHandoutEditor;
